import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  TbHome, TbDeviceMobileMessage, TbMail, TbBellRinging, TbLanguage,
  TbTag, TbFolderPlus, TbLibrary, TbSettings, TbLogout2, TbChevronDown,
  TbChevronRight, TbUserShield, TbUsers, TbList, TbChartBar, TbAppWindow,
  TbRobot, TbLayoutSidebarLeftCollapse, TbLayoutSidebarLeftExpand, TbCalendar, TbPlugConnected
} from 'react-icons/tb';
import { useAuth } from '../../services/check_login';
import '../../../assets/css/navbar.css';

const ADD_CONTENT_PATHS = ['/add-sms', '/add-popup', '/add-email', '/add-push-notification'];
const EMAIL_TEMPLATE_PATHS = ['/email-builder', '/email-templates/list'];
const SETTINGS_PATHS = ['/language', '/brand/create', '/roles/manage', '/users/manage', '/statuses/manage', '/placeholders/manage', '/integrations'];

const Navbar = ({ collapsed, onToggleSidebar }) => {
  const { user } = useAuth();
  const location = useLocation();
  const isActive = (path) => location.pathname === path;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSections, setOpenSections] = useState({
    addContent: ADD_CONTENT_PATHS.includes(location.pathname),
    emailTemplates: EMAIL_TEMPLATE_PATHS.includes(location.pathname),
    manageSettings: SETTINGS_PATHS.includes(location.pathname)
  });
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <>
      <nav className={`app-sidebar ${isMenuOpen ? 'show' : ''} ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <h3 className="logo">
            <a href="/">
              {collapsed ? (
                <img src="/logo_icon.png" alt="Content Craft Logo" />
              ) : (
                <>
                  <img src="/logo.png" alt="Content Craft Logo" className="logo-light" />
                  <img src="/logo_dark.png" alt="Content Craft Logo" className="logo-dark" />
                </>
              )}
            </a>
          </h3>
        </div>

        <div className="sidebar-menu">
          <ul className="menu-list">
            <li className="menu-section-row">
              <span className="menu-section-label">Main</span>
              <button
                className="sidebar-toggle-btn"
                onClick={onToggleSidebar}
                title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {collapsed ? <TbLayoutSidebarLeftExpand /> : <TbLayoutSidebarLeftCollapse />}
              </button>
            </li>
            <li className="menu-item">
              <Link to="/" className={`menu-link ${isActive('/') ? 'active' : ''}`}>
                <TbHome className="menu-icon" />
                <span>Dashboard</span>
              </Link>
            </li>
            
            {(user?.role?.permissions?.canViewAnalytics || user?.isAdmin) && (
              <li className="menu-item">
                <Link to="/analytics" className={`menu-link ${isActive('/analytics') ? 'active' : ''}`}>
                  <TbChartBar className="menu-icon" />
                  <span>Analytics</span>
                </Link>
              </li>
            )}
            
            {(user?.role?.permissions?.canUseAIAssistant || user?.isAdmin) && (
              <li className="menu-item">
                <Link to="/ai-assistant" className={`menu-link ${isActive('/ai-assistant') ? 'active' : ''}`}>
                  <TbRobot className="menu-icon" />
                  <span>AI Assistant</span>
                </Link>
              </li>
            )}

            {(user?.role?.permissions?.canCreateSMS || 
              user?.role?.permissions?.canCreateEmail || 
              user?.role?.permissions?.canCreatePopup || 
              user?.role?.permissions?.canCreatePushNotification || 
              user?.isAdmin) && (
              <li className="menu-item has-submenu">
                <div 
                  className="menu-link menu-toggle" 
                  onClick={() => toggleSection('addContent')}
                >
                  <div className="menu-text">
                    <TbFolderPlus className="menu-icon" />
                    <span>Create Content</span>
                  </div>
                  {openSections.addContent ? <TbChevronDown /> : <TbChevronRight />}
                </div>
                <ul className={`submenu ${openSections.addContent ? 'active' : ''}`}>
                {(user?.role?.permissions?.canCreateSMS || user?.isAdmin) && (
                  <li className="submenu-item">
                    <Link to="/add-sms" className={`submenu-link ${isActive('/add-sms') ? 'active' : ''}`}>
                      <TbDeviceMobileMessage className="submenu-icon" />
                      <span>Add SMS</span>
                    </Link>
                  </li>
                )}
                {(user?.role?.permissions?.canCreatePopup || user?.isAdmin) && (
                  <li className="submenu-item">
                    <Link to="/add-popup" className={`submenu-link ${isActive('/add-popup') ? 'active' : ''}`}>
                      <TbAppWindow className="submenu-icon" />
                      <span>Add Popup</span>
                    </Link>
                  </li>
                )}
                {(user?.role?.permissions?.canCreateEmail || user?.isAdmin) && (
                  <li className="submenu-item">
                    <Link to="/add-email" className={`submenu-link ${isActive('/add-email') ? 'active' : ''}`}>
                      <TbMail className="submenu-icon" />
                      <span>Add Email</span>
                    </Link>
                  </li>
                )}
                {(user?.role?.permissions?.canCreatePushNotification || user?.isAdmin) && (
                  <li className="submenu-item">
                    <Link to="/add-push-notification" className={`submenu-link ${isActive('/add-push-notification') ? 'active' : ''}`}>
                      <TbBellRinging className="submenu-icon" />
                      <span>Add Push Notification</span>
                    </Link>
                  </li>
                )}
              </ul>
              </li>
            )}

            {(user?.role?.permissions?.canViewLibrary || user?.isAdmin) && (
              <li className="menu-item">
                <Link to="/library/creatives" className={`menu-link ${isActive('/library/creatives') ? 'active' : ''}`}>
                  <TbLibrary className="menu-icon" />
                  <span>Library</span>
                </Link>
              </li>
            )}

            {(user?.role?.permissions?.canManageCampaigns || user?.isAdmin) && (
              <li className="menu-item">
                <Link to="/marketing-calendar" className={`menu-link ${isActive('/marketing-calendar') ? 'active' : ''}`}>
                  <TbCalendar className="menu-icon" />
                  <span>Marketing Calendar</span>
                </Link>
              </li>
            )}

            {(user?.role?.permissions?.canCreateEmailTemplates || 
              user?.role?.permissions?.canEditEmailTemplates || 
              user?.role?.permissions?.canDeleteEmailTemplates || 
              user?.isAdmin) && (
              <li className="menu-item has-submenu">
                <div 
                  className="menu-link menu-toggle" 
                  onClick={() => toggleSection('emailTemplates')}
                >
                  <div className="menu-text">
                    <TbMail className="menu-icon" />
                    <span>Template Builder</span>
                  </div>
                  {openSections.emailTemplates ? <TbChevronDown /> : <TbChevronRight />}
                </div>
                <ul className={`submenu ${openSections.emailTemplates ? 'active' : ''}`}>
                  {(user?.role?.permissions?.canCreateEmailTemplates || user?.isAdmin) && (
                    <li className="submenu-item">
                      <Link to="/email-builder" className={`submenu-link ${isActive('/email-builder') ? 'active' : ''}`}>
                        <TbMail className="submenu-icon" />
                        <span>Create Template</span>
                      </Link>
                    </li>
                  )}
                  {(user?.role?.permissions?.canEditEmailTemplates || 
                    user?.role?.permissions?.canDeleteEmailTemplates || 
                    user?.isAdmin) && (
                    <li className="submenu-item">
                      <Link to="/email-templates/list" className={`submenu-link ${isActive('/email-templates/list') ? 'active' : ''}`}>
                        <TbList className="submenu-icon" />
                        <span>Templates List</span>
                      </Link>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {(user?.role?.permissions?.canManageLanguages || 
              user?.role?.permissions?.canManageBrands || 
              user?.role?.permissions?.canManageIntegrations || 
              user?.isAdmin) && (
              <li className="menu-item has-submenu">
                <div 
                  className="menu-link menu-toggle" 
                  onClick={() => toggleSection('manageSettings')}
                >
                <div className="menu-text">
                  <TbSettings className="menu-icon" />
                  <span>Settings</span>
                </div>
                {openSections.manageSettings ? <TbChevronDown /> : <TbChevronRight />}
              </div>
              <ul className={`submenu ${openSections.manageSettings ? 'active' : ''}`}>
                {(user?.role?.permissions?.canManageLanguages || user?.isAdmin) && (
                  <li className="submenu-item">
                    <Link to="/language" className={`submenu-link ${isActive('/language') ? 'active' : ''}`}>
                      <TbLanguage className="submenu-icon" />
                      <span>Languages</span>
                    </Link>
                  </li>
                )}
                {(user?.role?.permissions?.canManageBrands || user?.isAdmin) && (
                  <li className="submenu-item">
                    <Link to="/brand/create" className={`submenu-link ${isActive('/brand/create') ? 'active' : ''}`}>
                      <TbTag className="submenu-icon" />
                      <span>Brands</span>
                    </Link>
                  </li>
                )}
                {(user?.role?.permissions?.canManageRoles || user?.isAdmin) && (
                  <li className="submenu-item">
                    <Link to="/roles/manage" className={`submenu-link ${isActive('/roles/manage') ? 'active' : ''}`}>
                      <TbUserShield className="submenu-icon" />
                      <span>Roles</span>
                    </Link>
                  </li>
                )}
                {(user?.role?.permissions?.canManageUsers || user?.isAdmin) && (
                  <li className="submenu-item">
                    <Link to="/users/manage" className={`submenu-link ${isActive('/users/manage') ? 'active' : ''}`}>
                      <TbUsers className="submenu-icon" />
                      <span>Users</span>
                    </Link>
                  </li>
                )}
                {user?.isAdmin && (
                  <li className="submenu-item">
                    <Link to="/statuses/manage" className={`submenu-link ${isActive('/statuses/manage') ? 'active' : ''}`}>
                      <TbList className="submenu-icon" />
                      <span>Statuses</span>
                    </Link>
                  </li>
                )}
                {(user?.role?.permissions?.canCreatePlaceholders || 
                  user?.role?.permissions?.canEditPlaceholders || 
                  user?.role?.permissions?.canDeletePlaceholders || 
                  user?.isAdmin) && (
                  <li className="submenu-item">
                    <Link to="/placeholders/manage" className={`submenu-link ${isActive('/placeholders/manage') ? 'active' : ''}`}>
                      <TbTag className="submenu-icon" />
                      <span>Placeholders</span>
                    </Link>
                  </li>
                )}
                {(user?.role?.permissions?.canManageIntegrations || user?.isAdmin) && (
                  <li className="submenu-item">
                    <Link to="/integrations" className={`submenu-link ${isActive('/integrations') ? 'active' : ''}`}>
                      <TbPlugConnected className="submenu-icon" />
                      <span>Integrations</span>
                    </Link>
                  </li>
                )}
              </ul>
              </li>
            )}
          </ul>
        </div>
        
        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <TbLogout2 className="logout-icon" />
            <span>Logout</span>
          </button>
        </div>
      </nav>
      
      {isMenuOpen && <div className="sidebar-overlay" onClick={toggleMenu}></div>}
      
      <button className="mobile-menu-btn" onClick={toggleMenu}>
        <span></span>
        <span></span>
        <span></span>
      </button>
    </>
  );
};

export default Navbar;